import { del, get, patch, post } from "@/services/axiosUitils";

const basePath = "/pts_app/purchase-approval";

export type PurchaseApprovalPriority = "High" | "Medium" | "Low";

export type PurchaseApprovalCurrency = "INR" | "US";

export type PurchaseApprovalStatus =
  | "Pending Approval"
  | "Approved"
  | "On-hold"
  | "Reject"
  | string;

export type PurchaseApprovalPurchaseStatus =
  | "In-progress"
  | "Not purchased"
  | "Purchased"
  | string;

export interface PurchaseApprovalAttachment {
  url?: string;
  name?: string;
  file_path?: string;
  file_size?: number;
  file_type?: string;
  document_id?: string;
}

export interface PurchaseApprovalItem {
  id: string;
  pr_number: string;
  project: string;
  project_name?: string;
  title: string;
  priority: PurchaseApprovalPriority | string;
  requested_date: string;
  expected_date: string;
  cost: number | string;
  currency?: PurchaseApprovalCurrency | string;
  frequency: string;
  requested_by: string;
  requested_by_name?: string;
  description?: string;
  attachments?: PurchaseApprovalAttachment[];
  attachments_link?: string;
  status: PurchaseApprovalStatus;
  purchase_status: PurchaseApprovalPurchaseStatus;
  management_reason?: string;
  account_reason?: string;
  created_on?: string;
  updated_on?: string;
}

export interface CreatePurchaseApprovalPayload {
  project: string;
  title: string;
  priority: PurchaseApprovalPriority;
  requested_date: string;
  expected_date: string;
  cost: string | number;
  currency: PurchaseApprovalCurrency;
  frequency: string;
  requested_by: string;
  description: string;
  attachments_link?: string;
  attachments?: File[];
}

export interface PurchaseApprovalCards {
  pending: number;
  approval: number;
  on_hold: number;
  reject: number;
}

export interface PurchaseApprovalAccountCards {
  approved_requests_this_month: number;
  total_cost_this_month: number | string;
  purchased: number;
  not_yet_purchased: number;
}

export interface PurchaseApprovalListParams {
  page?: number;
  page_size?: number;
  month?: string;
  year?: string | number;
  status?: string;
  project?: string;
  purchase_status?: string;
  requested_by?: string;
}

export interface PurchaseApprovalResponse {
  success?: { code?: string; message?: string };
  links?: { next?: string | null; previous?: string | null };
  count: number;
  page?: number;
  page_size?: number;
  results: PurchaseApprovalItem[];
  cards: PurchaseApprovalCards;
  account_cards: PurchaseApprovalAccountCards;
}

function unwrapList(response: unknown): PurchaseApprovalItem[] {
  if (!response) {
    return [];
  }

  if (Array.isArray(response)) {
    return response as PurchaseApprovalItem[];
  }

  const data = response as Record<string, unknown>;
  if (Array.isArray(data.data)) {
    return data.data as PurchaseApprovalItem[];
  }
  if (Array.isArray(data.results)) {
    return data.results as PurchaseApprovalItem[];
  }

  return [];
}

class PtsPurchaseApprovalService {
  /**
   * List purchase approvals.
   * Calls: GET /pts_app/purchase-approval/
   */
  getPurchaseApprovals = async (
    params?: PurchaseApprovalListParams
  ): Promise<PurchaseApprovalResponse> => {
    const response = await get<unknown>(`${basePath}/`, { params });
    const results = unwrapList(response);

    let count = results.length;
    let cards: PurchaseApprovalCards = {
      pending: 0,
      approval: 0,
      on_hold: 0,
      reject: 0,
    };
    let accountCards: PurchaseApprovalAccountCards = {
      approved_requests_this_month: 0,
      total_cost_this_month: "0.00",
      purchased: 0,
      not_yet_purchased: 0,
    };

    if (response && typeof response === "object" && !Array.isArray(response)) {
      const data = response as Record<string, unknown>;
      if (typeof data.count === "number") {
        count = data.count;
      }
      if (data.cards && typeof data.cards === "object") {
        cards = { ...cards, ...(data.cards as any) };
      }
      if (data.account_cards && typeof data.account_cards === "object") {
        accountCards = { ...accountCards, ...(data.account_cards as any) };
      }
    }

    return {
      results,
      count,
      cards,
      account_cards: accountCards,
    };
  };

  /**
   * Get a single purchase approval.
   * Calls: GET /pts_app/purchase-approval/{id}/
   */
  getPurchaseApprovalById = async (
    id: string
  ): Promise<PurchaseApprovalItem | undefined> => {
    const response = await get<unknown>(`${basePath}/${id}/`);
    if (!response || typeof response !== "object") {
      return undefined;
    }

    const data = response as Record<string, unknown>;
    if (typeof data.id === "string") {
      return data as unknown as PurchaseApprovalItem;
    }
    if (data.data && typeof data.data === "object") {
      return data.data as PurchaseApprovalItem;
    }

    return undefined;
  };

  /**
   * Create a purchase approval (multipart/form-data).
   * Calls: POST /pts_app/purchase-approval/
   */
  createPurchaseApproval = async (
    payload: CreatePurchaseApprovalPayload
  ): Promise<PurchaseApprovalItem | undefined> => {
    const formData = this.buildFormData(payload);
    return await post<PurchaseApprovalItem>(`${basePath}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      notifyMessage: true,
    });
  };

  /**
   * Update a purchase approval form (multipart/form-data).
   * Calls: PATCH /pts_app/purchase-approval/{id}/
   */
  updatePurchaseApprovalForm = async (
    id: string,
    payload: CreatePurchaseApprovalPayload
  ): Promise<PurchaseApprovalItem | undefined> => {
    const formData = this.buildFormData(payload);
    return await patch<PurchaseApprovalItem>(`${basePath}/${id}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      notifyMessage: true,
    });
  };

  /**
   * Update purchase approval fields / status.
   * Calls: PATCH /pts_app/purchase-approval/{id}/
   */
  updatePurchaseApproval = async (
    id: string,
    payload: Record<string, unknown>
  ): Promise<PurchaseApprovalItem | undefined> => {
    return await patch<PurchaseApprovalItem>(`${basePath}/${id}/`, payload, {
      notifyMessage: true,
    });
  };

  /**
   * Delete a purchase approval.
   * Calls: DELETE /pts_app/purchase-approval/{id}/
   */
  deletePurchaseApproval = async (id: string): Promise<unknown> => {
    return await del(`${basePath}/${id}/`, { notifyMessage: true });
  };

  /**
   * Bulk update purchase approvals status.
   * Calls: PATCH /pts_app/purchase-approval/bulk-status/
   */
  bulkUpdateStatus = async (
    ids: string[],
    status: PurchaseApprovalStatus
  ): Promise<unknown> => {
    return await patch(
      `${basePath}/bulk-status/`,
      { ids, status },
      { notifyMessage: true }
    );
  };

  approvePurchaseApproval = async (id: string) => {
    return this.updatePurchaseApproval(id, { status: "Approved" });
  };

  rejectPurchaseApproval = async (id: string) => {
    return this.updatePurchaseApproval(id, { status: "Reject" });
  };

  private buildFormData = (
    payload: CreatePurchaseApprovalPayload
  ): FormData => {
    const formData = new FormData();
    formData.append("project", payload.project);
    formData.append("title", payload.title);
    formData.append("priority", payload.priority);
    formData.append("requested_date", payload.requested_date);
    formData.append("expected_date", payload.expected_date);
    formData.append("cost", String(payload.cost));
    formData.append("currency", payload.currency);
    formData.append("frequency", payload.frequency);
    formData.append("requested_by", payload.requested_by);
    formData.append("description", payload.description);
    if (payload.attachments_link) {
      formData.append("attachments_link", payload.attachments_link);
    }

    (payload.attachments ?? []).forEach(file => {
      formData.append("attachments", file);
    });

    return formData;
  };
}

const instance = new PtsPurchaseApprovalService();
export default instance;
